import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit, 
  Camera, 
  Calendar, 
  TrendingUp, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  User,
  Phone,
  Mail,
  Users,
  X
} from 'lucide-react';
import Layout from '../components/Layout';
import { useApp } from '../contexts/AppContext';
import { Student, AttendanceRecord, BehaviorIncident, StudentStreak } from '../types';
import { format, startOfWeek, endOfWeek, subDays, addDays } from 'date-fns';
import { studentService, attendanceService, behaviorService } from '../services/apiService';
import toast from 'react-hot-toast';

// Helper function to calculate attendance streak
const calculateStreak = (attendanceRecords: AttendanceRecord[]): StudentStreak => {
  const sortedRecords = attendanceRecords
    .filter(record => record.status === 'present')
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  
  let currentStreak = 0;
  let longestStreak = 0;
  let tempStreak = 0;
  
  for (let i = 0; i < sortedRecords.length; i++) {
    if (i === 0 || 
        new Date(sortedRecords[i - 1].date).getTime() - new Date(sortedRecords[i].date).getTime() === 86400000) {
      currentStreak++;
      tempStreak++;
    } else {
      longestStreak = Math.max(longestStreak, tempStreak);
      tempStreak = 1;
    }
  }
  
  longestStreak = Math.max(longestStreak, tempStreak);
  
  return {
    studentId: '',
    currentStreak,
    longestStreak,
    lastAttendanceDate: sortedRecords[0]?.date,
    flags: []
  };
};

const StudentProfilePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { students, classes, updateStudent } = useApp();
  const [student, setStudent] = useState<Student | null>(null);
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [behaviorIncidents, setBehaviorIncidents] = useState<BehaviorIncident[]>([]);
  const [streak, setStreak] = useState<StudentStreak | null>(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showIncidentDetailsModal, setShowIncidentDetailsModal] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState<BehaviorIncident | null>(null);
  const [dateRange, setDateRange] = useState({
    start: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd')
  });
  const [editFormData, setEditFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    parentName: '',
    parentEmail: '',
    notes: ''
  });

  useEffect(() => {
    if (id) {
      loadStudentData();
    }
  }, [id, dateRange]);

  const loadStudentData = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const [studentData, attendanceData, behaviorData] = await Promise.all([
        studentService.getById(id),
        attendanceService.getByStudent(id, dateRange.start, dateRange.end),
        behaviorService.getByStudent(id, dateRange.start, dateRange.end)
      ]);
      
      setStudent(studentData);
      setAttendanceRecords(attendanceData);
      setBehaviorIncidents(behaviorData);
      // Calculate streak from attendance data
      const streak = calculateStreak(attendanceData);
      setStreak(streak);
    } catch (error) {
      toast.error('Failed to load student data');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!student || !event.target.files?.[0]) return;
    
    try {
      const file = event.target.files[0];
      const photoUrl = await studentService.uploadPhoto(student.id, file);
      await updateStudent(student.id, { photo: photoUrl });
      setStudent(prev => prev ? { ...prev, photo: photoUrl } : null);
      toast.success('Photo updated successfully');
    } catch (error) {
      toast.error('Failed to upload photo');
    }
  };

  const getAttendanceStats = () => {
    const total = attendanceRecords.length;
    const present = attendanceRecords.filter(r => r.status === 'present').length;
    const absent = attendanceRecords.filter(r => r.status === 'absent').length;
    const attendanceRate = total > 0 ? (present / total) * 100 : 0;
    
    return { total, present, absent, attendanceRate: Math.round(attendanceRate) };
  };

  const getBehaviorStats = () => {
    const total = behaviorIncidents.length;
    const byType = behaviorIncidents.reduce((acc, incident) => {
      acc[incident.type] = (acc[incident.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    return { total, byType };
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'present':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'absent':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'no-class':
        return <Clock className="h-4 w-4 text-gray-600 dark:text-gray-400" />;
      default:
        return <XCircle className="h-4 w-4 text-gray-400 dark:text-gray-500" />;
    }
  };


  const getIncidentLabelStyle = (incident: BehaviorIncident) => {
    const baseClasses = "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity";
    
    if (incident.resolved) {
      return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
    }
    
    switch (incident.severity) {
      case 'high':
        return `${baseClasses} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
      case 'medium':
        return `${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
      case 'low':
        return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200`;
    }
  };

  const handleIncidentClick = (incident: BehaviorIncident) => {
    setSelectedIncident(incident);
    setShowIncidentDetailsModal(true);
  };

  const handleEditClick = () => {
    if (student) {
      setEditFormData({
        firstName: student.firstName,
        lastName: student.lastName,
        email: student.email || '',
        phone: student.phone || '',
        dateOfBirth: student.dateOfBirth || '',
        parentName: student.parentName || '',
        parentEmail: student.parentEmail || '',
        notes: student.notes || ''
      });
      setShowEditModal(true);
    }
  };

  const handleEditSave = async () => {
    if (!student) return;
    
    try {
      await updateStudent(student.id, editFormData);
      toast.success('Student updated successfully');
      setShowEditModal(false);
      loadStudentData();
    } catch (error) {
      toast.error('Failed to update student');
    }
  };

  const getFlagColor = (type: 'red' | 'green') => {
    return type === 'red' ? 'text-red-600' : 'text-green-600';
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="space-y-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-32 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!student) {
    return (
      <Layout>
        <div className="p-6">
          <div className="text-center py-12">
            <User className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Student not found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">The student you're looking for doesn't exist.</p>
            <div className="mt-6">
              <Link to="/students" className="btn-primary">
                Back to Students
              </Link>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const attendanceStats = getAttendanceStats();
  const behaviorStats = getBehaviorStats();
  // Class information not available since students don't have direct classId

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <Link to="/students" className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300">
              <ArrowLeft className="h-6 w-6" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{student.firstName} {student.lastName}</h1>
              <p className="text-gray-600 dark:text-gray-300">{student.email || 'No email'}</p>
            </div>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowPhotoModal(true)}
              className="btn-secondary flex items-center"
            >
              <Camera className="h-5 w-5 mr-2" />
              Change Photo
            </button>
            <button
              onClick={handleEditClick}
              className="btn-primary flex items-center"
            >
              <Edit className="h-5 w-5 mr-2" />
              Edit Profile
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Student Info */}
          <div className="lg:col-span-1">
            <div className="card">
              <div className="text-center">
                <div className="relative inline-block">
                  {student.photo ? (
                    <img
                      src={student.photo}
                      alt={`${student.firstName} ${student.lastName}`}
                      className="h-32 w-32 rounded-full object-cover mx-auto"
                    />
                  ) : (
                    <div className="h-32 w-32 rounded-full bg-gray-200 flex items-center justify-center mx-auto">
                      <User className="h-16 w-16 text-gray-400" />
                    </div>
                  )}
                </div>
                <h3 className="mt-4 text-xl font-semibold text-gray-900 dark:text-white">{student.firstName} {student.lastName}</h3>
                <p className="text-gray-600 dark:text-gray-300">{student.email || 'No email'}</p>
              </div>

              <div className="mt-6 space-y-4">
                {student.email && (
                  <div className="flex items-center">
                    <Mail className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-3" />
                    <span className="text-gray-600 dark:text-gray-300">{student.email}</span>
                  </div>
                )}
                {student.phone && (
                  <div className="flex items-center">
                    <Phone className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-3" />
                    <span className="text-gray-600 dark:text-gray-300">{student.phone}</span>
                  </div>
                )}
                {student.parentName && (
                  <div className="flex items-center">
                    <Users className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-3" />
                    <div>
                      <p className="text-gray-600 dark:text-gray-300">{student.parentName}</p>
                    </div>
                  </div>
                )}
                {student.dateOfBirth && (
                  <div className="flex items-center">
                    <Calendar className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-3" />
                    <span className="text-gray-600 dark:text-gray-300">
                      {(() => {
                        try {
                          // Handle different date formats
                          let date;
                          if (typeof student.dateOfBirth === 'string') {
                            // If it's in MM/DD/YYYY format, parse it properly
                            if (student.dateOfBirth.includes('/')) {
                              const [month, day, year] = student.dateOfBirth.split('/');
                              date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                            } else {
                              // Try standard date parsing
                              date = new Date(student.dateOfBirth);
                            }
                          } else {
                            date = new Date(student.dateOfBirth);
                          }
                          
                          // Check if date is valid
                          if (isNaN(date.getTime())) {
                            return student.dateOfBirth; // Return original string if parsing fails
                          }
                          
                          return format(date, 'MMM d, yyyy');
                        } catch (error) {
                          return student.dateOfBirth; // Return original string if any error occurs
                        }
                      })()}
                    </span>
                  </div>
                )}
              </div>

              {student.notes && (
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Notes</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">{student.notes}</p>
                </div>
              )}
            </div>

            {/* Streak Info */}
            {streak && (
              <div className="card mt-6">
                <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Attendance Streak</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Current Streak</span>
                    <span className="font-semibold text-primary-600">{streak.currentStreak} days</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Longest Streak</span>
                    <span className="font-semibold text-gray-900 dark:text-white">{streak.longestStreak} days</span>
                  </div>
                  {streak.lastAttendanceDate && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">Last Attendance</span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {format(new Date(streak.lastAttendanceDate), 'MMM d')}
                      </span>
                    </div>
                  )}
                </div>

                {streak.flags.length > 0 && (
                  <div className="mt-4">
                    <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Flags</h5>
                    <div className="space-y-1">
                      {streak.flags.map((flag, index) => (
                        <div key={index} className="flex items-center justify-between text-sm">
                          <span className={getFlagColor(flag.type)}>
                            {flag.type.toUpperCase()}
                          </span>
                          <span className="text-gray-600 dark:text-gray-300">{flag.reason}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="card text-center">
                <div className="text-2xl font-bold text-primary-600">{attendanceStats.attendanceRate}%</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Attendance Rate</div>
                <div className="text-xs text-gray-400 dark:text-gray-500">
                  {attendanceStats.present}/{attendanceStats.total} days
                </div>
              </div>
              <div className="card text-center">
                <div className="text-2xl font-bold text-green-600">{streak?.currentStreak || 0}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Current Streak</div>
                <div className="text-xs text-gray-400 dark:text-gray-500">consecutive days</div>
              </div>
              <div className="card text-center">
                <div className="text-2xl font-bold text-red-600">{behaviorStats.total}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Behavior Incidents</div>
                <div className="text-xs text-gray-400 dark:text-gray-500">in selected period</div>
              </div>
            </div>

            {/* Date Range Filter */}
            <div className="card">
              <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Date Range</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                    className="input-field"
                  />
                </div>
              </div>
            </div>

            {/* Attendance History */}
            <div className="card">
              <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Attendance History</h4>
              {attendanceRecords.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-4">No attendance records found</p>
              ) : (
                <div className="space-y-2">
                  {attendanceRecords.map((record) => (
                    <div key={record.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center space-x-3">
                        {getStatusIcon(record.status)}
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {format(new Date(record.date), 'MMM d, yyyy')}
                          </p>
                          {record.notes && (
                            <p className="text-sm text-gray-500 dark:text-gray-400">{record.notes}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          record.status === 'present' ? 'bg-green-100 text-green-800' :
                          record.status === 'absent' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {record.status.replace('-', ' ').toUpperCase()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Behavior Incidents */}
            {behaviorIncidents.length > 0 && (
              <div className="card">
                <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Behavior Incidents</h4>
                <div className="flex flex-wrap gap-2">
                  {behaviorIncidents.map((incident) => (
                    <span
                      key={incident.id}
                      onClick={() => handleIncidentClick(incident)}
                      className={getIncidentLabelStyle(incident)}
                      title={`${incident.type.replace('-', ' ').toUpperCase()} - ${incident.severity} - Click for details`}
                    >
                      {incident.type.replace('-', ' ').toUpperCase()}
                      {incident.resolved && ' ✓'}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Edit Student Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white dark:bg-gray-800">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Edit Student Profile
                  </h3>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={editFormData.firstName}
                      onChange={(e) => setEditFormData({ ...editFormData, firstName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={editFormData.lastName}
                      onChange={(e) => setEditFormData({ ...editFormData, lastName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={editFormData.email}
                      onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={editFormData.phone}
                      onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      value={editFormData.dateOfBirth}
                      onChange={(e) => setEditFormData({ ...editFormData, dateOfBirth: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Parent Name
                    </label>
                    <input
                      type="text"
                      value={editFormData.parentName}
                      onChange={(e) => setEditFormData({ ...editFormData, parentName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Parent Email
                    </label>
                    <input
                      type="email"
                      value={editFormData.parentEmail}
                      onChange={(e) => setEditFormData({ ...editFormData, parentEmail: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={editFormData.notes}
                    onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleEditSave}
                    className="btn-primary"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Photo Upload Modal */}
        {showPhotoModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Update Photo</h3>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="w-full"
                />
                <div className="flex justify-end space-x-3 mt-4">
                  <button
                    onClick={() => setShowPhotoModal(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Incident Details Modal */}
        {showIncidentDetailsModal && selectedIncident && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Incident Details
                  </h3>
                  <button
                    onClick={() => setShowIncidentDetailsModal(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Type
                    </label>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getIncidentLabelStyle(selectedIncident)}`}>
                      {selectedIncident.type.replace('-', ' ').toUpperCase()}
                    </span>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Severity
                    </label>
                    <span className={`inline-flex items-center px-2 py-1 rounded text-sm ${
                      selectedIncident.severity === 'high'
                        ? 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200'
                        : selectedIncident.severity === 'medium'
                        ? 'bg-yellow-200 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200'
                        : 'bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-200'
                    }`}>
                      {selectedIncident.severity.toUpperCase()}
                    </span>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description
                    </label>
                    <p className="text-sm text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                      {selectedIncident.description}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Status
                    </label>
                    <span className={`inline-flex items-center px-2 py-1 rounded text-sm ${
                      selectedIncident.resolved
                        ? 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200'
                        : 'bg-yellow-200 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200'
                    }`}>
                      {selectedIncident.resolved ? 'RESOLVED' : 'PENDING'}
                    </span>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Time
                    </label>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {format(new Date(selectedIncident.timestamp), 'MMM d, yyyy h:mm a')}
                    </p>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => setShowIncidentDetailsModal(false)}
                    className="btn-primary"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default StudentProfilePage;
